/**
 * Shared shapes for the On-Order tab. Mirrors the backend reporting service
 * (ReorderSignal + OnOrderSummary) — kept as local types so the frontend stays
 * decoupled from the backend package, matching the OverviewTab precedent.
 */
import type { TFunction } from 'i18next';
import type { BuyingAction } from '../../lib/reporting-format';

export type { BuyingAction };

/** i18n-defaulted label for a buying action (shared across On-Order + Overview). */
export function buyingActionLabel(action: BuyingAction, t: TFunction): string {
  switch (action) {
    case 'order_now':
      return t('reporting.v2.action.orderNow', 'Order now');
    case 'arriving_late':
      return t('reporting.v2.action.arrivingLate', 'Arriving late');
    case 'covered':
      return t('reporting.v2.action.covered', 'Covered');
    default:
      return t('reporting.v2.action.ok', 'OK');
  }
}

export type EtaBucketKey = 'overdue' | 'this_week' | 'next_30' | 'later' | 'no_eta';

/** One at-risk SKU from get_reporting_reorder_signals, enriched with inbound. */
export type ReorderSignalRow = {
  skuId: string;
  sku: string;
  category: string;
  productType: string;
  /** null in the All-Locations view (BLU-621: rows are pooled per SKU). */
  locationId: string | null;
  onHand: number;
  incoming: number;
  notifyThreshold: number | null;
  runRate: number;
  daysOfCover: number | null;
  projectedDaysOfCover: number | null;
  nextEtaDate: string | null;
  buyingAction: BuyingAction;
  belowThreshold: boolean;
  reasons: string[];
};

export type OnOrderEtaBucket = {
  bucket: EtaBucketKey;
  units: number;
  value: number;
};

export type OnOrderLine = {
  poId: string;
  poNumber: string | null;
  sku: string;
  skuId: string;
  category: string;
  supplierName: string | null;
  etaDate: string | null;
  etaBucket: EtaBucketKey;
  outstandingQty: number;
  unitCost: number | null;
  poCurrency: string | null;
  value: number;
};

export type OnOrderSummary = {
  totals: {
    onOrderUnits: number;
    committedValue: number;
    poCount: number;
    missingEtaCount: number;
    valueThisWeek: number;
  };
  etaBuckets: OnOrderEtaBucket[];
  lines: OnOrderLine[];
};

/** Display order + i18n-defaulted labels for the ETA buckets. */
export const ETA_BUCKET_ORDER: EtaBucketKey[] = [
  'overdue',
  'this_week',
  'next_30',
  'later',
  'no_eta',
];
